import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { queryKeys } from '../queries/queryKeys';
import type { TablesInsert, TablesUpdate } from '@/integrations/supabase/types';

type AccountInsert = TablesInsert<'accounts'>;
type AccountUpdate = TablesUpdate<'accounts'>;
type WalletInsert = TablesInsert<'wallets'>;
type WalletUpdate = TablesUpdate<'wallets'>;

/**
 * Add a new account
 */
export function useAddAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (account: Omit<AccountInsert, 'user_id'>) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('accounts')
        .insert({ ...account, user_id: user.id })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.accounts.all });
    },
  });
}

/**
 * Update an existing account
 */
export function useUpdateAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: AccountUpdate & { id: string }) => {
      const { data, error } = await supabase
        .from('accounts')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.accounts.all });
    },
  });
}

/**
 * Delete an account
 */
export function useDeleteAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('accounts')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.accounts.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.wallets.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.transactions.all });
    },
  });
}

/**
 * Add a new wallet
 */
export function useAddWallet() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (wallet: Omit<WalletInsert, 'user_id'>) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('wallets')
        .insert({ ...wallet, user_id: user.id })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.wallets.all });
    },
  });
}

/**
 * Update an existing wallet
 */
export function useUpdateWallet() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: WalletUpdate & { id: string }) => {
      const { data, error } = await supabase
        .from('wallets')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.wallets.all });
    },
  });
}

/**
 * Delete a wallet
 */
export function useDeleteWallet() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('wallets')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.wallets.all });
    },
  });
}

/**
 * Add a funding source to an account (auto-assigns next priority)
 */
export function useAddFundingSource() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ accountId, fundingAccountId }: { accountId: string; fundingAccountId: string }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get current max priority for this account
      const { data: existing } = await supabase
        .from('account_funding_sources')
        .select('priority')
        .eq('account_id', accountId)
        .order('priority', { ascending: false })
        .limit(1);

      const nextPriority = existing && existing.length > 0 ? existing[0].priority + 1 : 1;

      const { data, error } = await supabase
        .from('account_funding_sources')
        .insert({ user_id: user.id, account_id: accountId, funding_account_id: fundingAccountId, priority: nextPriority })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.accounts.fundingSources() });
    },
  });
}

/**
 * Remove a funding source and renumber remaining priorities
 */
export function useRemoveFundingSource() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, accountId }: { id: string; accountId: string }) => {
      const { error } = await supabase
        .from('account_funding_sources')
        .delete()
        .eq('id', id);

      if (error) throw error;

      // Re-number remaining priorities to keep them contiguous
      const { data: remaining } = await supabase
        .from('account_funding_sources')
        .select('id')
        .eq('account_id', accountId)
        .order('priority');

      if (remaining) {
        for (let i = 0; i < remaining.length; i++) {
          await supabase
            .from('account_funding_sources')
            .update({ priority: i + 1 })
            .eq('id', remaining[i].id);
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.accounts.fundingSources() });
    },
  });
}

/**
 * Reorder a funding source up or down by swapping priorities with its neighbour
 */
export function useReorderFundingSource() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, accountId, direction }: { id: string; accountId: string; direction: 'up' | 'down' }) => {
      const { data: sources, error } = await supabase
        .from('account_funding_sources')
        .select('id, priority')
        .eq('account_id', accountId)
        .order('priority');

      if (error) throw error;
      if (!sources) return;

      const idx = sources.findIndex(s => s.id === id);
      if (idx < 0) return;

      const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
      if (swapIdx < 0 || swapIdx >= sources.length) return;

      const a = sources[idx];
      const b = sources[swapIdx];

      // Swap priorities (no UNIQUE constraint on priority, so a direct swap is safe)
      await supabase.from('account_funding_sources').update({ priority: b.priority }).eq('id', a.id);
      await supabase.from('account_funding_sources').update({ priority: a.priority }).eq('id', b.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.accounts.fundingSources() });
    },
  });
}
